import React, { useState, useEffect, useContext } from "react";
import { PopupContext } from "../../index";
import { getDeletePopup } from "../../view/popup/PopupContainer";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ThemeCreatorModal from "./ThemeCreatorModal";
import ModalListTable from "../../view/ModalListTable";
import { listThemes, deleteTheme } from "./themeService";
import { showSuccessToast, showDangerToast } from "../../validation/ToastContainer";
import "./theme.scss";

const ThemeListModal = ({ isOpen, onClose }) => {
    const [themes, setThemes] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [view, setView] = useState("LIST");
    const [selectedTheme, setSelectedTheme] = useState(null);
    const { setPopup } = useContext(PopupContext);

    useEffect(() => {
        if (isOpen && view === "LIST") {
            loadThemes();
        }
    }, [isOpen, view]);

    const loadThemes = async () => {
        setIsLoading(true);
        try {
            const response = await listThemes();
            if (response.ok) {
                const data = await response.json();
                setThemes(data);
            } else {
                showDangerToast("Failed to load themes", "Error");
            }
        } catch (error) {
            console.error("Error loading themes:", error);
            showDangerToast("Error loading themes", "Error");
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateNew = () => {
        setSelectedTheme(null);
        setView("EDITOR");
    };

    const handleEdit = (theme) => {
        setSelectedTheme(theme);
        setView("EDITOR");
    };

    const handleDelete = (themeId) => {
        setPopup(getDeletePopup(
            "Are you sure you want to delete this template?",
            "Delete Template",
            async () => {
                try {
                    const response = await deleteTheme(themeId);
                    if (response.ok) {
                        showSuccessToast("Template deleted successfully", "Success");
                        loadThemes();
                    } else {
                        showDangerToast("Failed to delete template", "Error");
                    }
                } catch (error) {
                    console.error("Error deleting template:", error);
                    showDangerToast("Error deleting template", "Error");
                }
            },
            undefined,
            "Delete Template"
        ));
    };

    const handleEditorClose = () => {
        setView("LIST");
    };

    const handleEditorSave = () => {
        setView("LIST");
        loadThemes();
    };

    const columns = [
        {
            key: "name",
            label: "Template Name",
            className: "theme-list-modal__name-cell",
            searchValue: (theme) => theme.name,
            render: (theme) => (
                <span className="theme-list-modal__primary-text">{theme.name}</span>
            )
        },
        {
            key: "description",
            label: "Description",
            className: "theme-list-modal__description-cell",
            searchValue: (theme) => theme.description || "",
            render: (theme) => (
                <span className="theme-list-modal__secondary-text">{theme.description || "-"}</span>
            )
        },
        {
            key: "actions",
            label: "Actions",
            className: "theme-list-modal__actions-cell",
            headerClassName: "theme-list-modal__actions-cell",
            render: (theme) => (
                <div className="oct_modal-list__action-group">
                    <button
                        type="button"
                        className="oct_modal-list__action-btn"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleEdit(theme);
                        }}
                        title="Edit Template"
                    >
                        <EditIcon fontSize="small" />
                    </button>
                    <button
                        type="button"
                        className="oct_modal-list__action-btn oct_modal-list__action-btn--danger"
                        onClick={(event) => {
                            event.stopPropagation();
                            handleDelete(theme.id);
                        }}
                        title="Delete Template"
                    >
                        <DeleteIcon fontSize="small" />
                    </button>
                </div>
            )
        }
    ];

    if (!isOpen) {
        return null;
    }

    if (view === "EDITOR") {
        return (
            <ThemeCreatorModal
                isOpen={true}
                onClose={handleEditorClose}
                onSave={handleEditorSave}
                editTheme={selectedTheme}
            />
        );
    }

    return (
        <div className="modal oct_display-block modal-v2-root">
            <div className="modal oct_modal modal-v2-overlay">
                <div className="modal-dialog modal-xl modal-dialog-centered modal-v2-dialog" aria-modal="true">
                    <div className="modal-content modal-v2-shell create-report-modal theme-list-modal">
                        <div className="modal-v2-header theme-list-modal-header">
                            <div className="modal-v2-header-top">
                                <div className="modal-v2-header-copy">
                                    <h2 className="modal-v2-heading theme-list-modal-title">Report Templates</h2>
                                </div>
                                <div className="theme-list-modal-header-actions">
                                    <button
                                        type="button"
                                        className="modal-v2-btn modal-v2-btn-primary theme-list-modal-create-btn"
                                        onClick={handleCreateNew}
                                    >
                                        <AddIcon fontSize="small" className="mr-1" /> Create Template
                                    </button>
                                </div>
                            </div>
                            <button
                                type="button"
                                className="modal-v2-close theme-list-modal-close"
                                aria-label="Close"
                                onClick={onClose}
                            >
                                <span aria-hidden="true"><CloseIcon /></span>
                            </button>
                        </div>
                        <div className="modal-body modal-v2-body theme-list-modal-body">
                            <ModalListTable
                                items={themes}
                                columns={columns}
                                loading={isLoading}
                                variant="schedule"
                                searchPlaceholder="Search templates..."
                                emptyMessage="No templates found. Create one to get started."
                                noResultsMessage="No templates match your search."
                                getSearchValue={(theme) => `${theme.name || ""} ${theme.description || ""}`}
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ThemeListModal;
